import { useEffect, useMemo } from 'react';
import { Search, X } from 'lucide-react';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { CHAT_SEARCH_MAX_QUERY_LENGTH, chatSearchMinQueryLength } from '@fias/platform-resources';
import { useChatMessageSearch } from '../hooks/useChatMessageSearch';
import { useChatSearchControls } from '../hooks/useChatSearchControls';
import {
  NO_MATCH_HINT,
  operatorHint,
  searchTermsForHighlight,
  type AbsorbableOperator,
} from './message-search-utils';
import { MessageSearchResultRow } from './MessageSearchResultRow';
import { SenderFilter } from './SenderFilter';
import { SearchMatchModeToggle } from './SearchMatchModeToggle';
import { SearchHasFilter } from './SearchHasFilter';
import { SearchDateFilter } from './SearchDateFilter';
import { SearchErrorState } from './SearchErrorState';
import type { ChatChannelMemberResponse } from '@/lib/api';

/** Inside one conversation `in:` has nothing to pick; it stays plain text. */
const CHANNEL_OPERATORS: ReadonlySet<AbsorbableOperator> = new Set([
  'from',
  'after',
  'before',
  'has',
]);
const OPERATOR_HINT = operatorHint(CHANNEL_OPERATORS);

/** How long the box settles before the server is asked; one place per surface. */
export const CHAT_SEARCH_DEBOUNCE_MS = 350;

interface ChannelSearchPanelProps {
  channelId: string | null;
  /** Name shown in the placeholder, e.g. "Search in #general". */
  channelLabel: string;
  /**
   * Channel members — suggestions for the "From" filter, not a limit on it. The
   * filter also accepts a typed handle, so senders past the loaded member page
   * and people who have left the channel stay reachable.
   */
  members?: ChatChannelMemberResponse[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Scroll the conversation to this message. Optional — without it the results
   * are a read-only list (the embedded guild pane has no jump plumbing).
   */
  onSelectResult?: (messageId: string) => void;
}

/**
 * In-channel message search: text in message content, a "From" filter on
 * one sender, or both together. Either filter stands alone, so picking a sender
 * with an empty box lists everything they wrote here. Clicking a result closes
 * the panel and jumps to the message when it's inside the loaded window.
 */
export function ChannelSearchPanel({
  channelId,
  channelLabel,
  members,
  open,
  onOpenChange,
  onSelectResult,
}: ChannelSearchPanelProps) {
  const {
    rawQuery,
    setRawQuery,
    senderFilter,
    setSenderFilter,
    matchMode,
    setMatchMode,
    contentFilter,
    setContentFilter,
    dayRange,
    setDayRange,
    ready,
    filters,
    reset,
  } = useChatSearchControls(CHANNEL_OPERATORS);
  const query = useDebouncedValue(rawQuery, CHAT_SEARCH_DEBOUNCE_MS);

  // Start each visit from a clean slate rather than reopening onto a stale
  // result set from a previous channel.
  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  const {
    results,
    total,
    isLoading,
    isError,
    errorKind,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useChatMessageSearch(channelId, query, filters, open && ready);

  const trimmed = query.trim();
  const minLength = chatSearchMinQueryLength(matchMode);
  const hasTerms = trimmed.length >= minLength;
  const isQueryable = (hasTerms || !!senderFilter || !!contentFilter) && ready;
  const isDebouncing = rawQuery.trim() !== trimmed;
  // Only the text terms get highlighted; a sender filter has nothing to mark.
  const terms = useMemo(
    () => searchTermsForHighlight(trimmed, hasTerms, matchMode),
    [trimmed, hasTerms, matchMode],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
          <Search className="h-4 w-4 text-primary flex-shrink-0" />
          <DialogTitle className="text-base font-semibold">Search messages</DialogTitle>
        </div>
        <DialogDescription className="sr-only">
          Search this conversation by message text, by sender, or both.
        </DialogDescription>

        <div className="px-4 pt-3 pb-2">
          <div className="relative">
            <input
              type="search"
              value={rawQuery}
              onChange={(e) => setRawQuery(e.target.value)}
              maxLength={CHAT_SEARCH_MAX_QUERY_LENGTH}
              // The panel is only mounted-open on demand, so grabbing focus here
              // is what the user asked for by opening it.
              autoFocus
              placeholder={`Search in ${channelLabel}`}
              aria-label={`Search messages in ${channelLabel}`}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 pr-8 text-sm outline-none focus:ring-2 focus:ring-primary/40"
            />
            {rawQuery.length > 0 && (
              <button
                type="button"
                onClick={() => setRawQuery('')}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs text-muted-foreground flex-shrink-0">From</span>
            <SenderFilter members={members ?? []} value={senderFilter} onChange={setSenderFilter} />
          </div>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs text-muted-foreground flex-shrink-0">Match</span>
            <SearchMatchModeToggle value={matchMode} onChange={setMatchMode} />
          </div>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs text-muted-foreground flex-shrink-0">Has</span>
            <SearchHasFilter value={contentFilter} onChange={setContentFilter} />
          </div>
          <div className="mt-2">
            <SearchDateFilter value={dayRange} onChange={setDayRange} />
          </div>
          {isQueryable && !isLoading && !isError && (
            <p className="text-[11px] text-muted-foreground mt-2">
              {total} {total === 1 ? 'match' : 'matches'}
            </p>
          )}
        </div>

        <div className="max-h-[55vh] overflow-y-auto border-t border-border">
          {!isQueryable ? (
            <div className="text-center py-10 px-6">
              <Search className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                Type at least {minLength} characters to search
              </p>
              {matchMode === 'contains' && (
                <p className="text-xs text-muted-foreground/70 mt-1">
                  For shorter words, switch to “Whole word”.
                </p>
              )}
              <p className="text-xs text-muted-foreground/70 mt-1">
                Or pick someone under “From” to see everything they wrote here, or a content filter
                under “Has”.
              </p>
              <p className="text-xs text-muted-foreground/70 mt-1">{OPERATOR_HINT}</p>
            </div>
          ) : isLoading || isDebouncing ? (
            <p className="text-sm text-muted-foreground text-center py-10">Searching…</p>
          ) : isError ? (
            <SearchErrorState kind={errorKind} />
          ) : results.length === 0 ? (
            <div className="text-center py-10 px-6">
              <p className="text-sm text-muted-foreground">No messages found</p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                {hasTerms
                  ? NO_MATCH_HINT[matchMode]
                  : senderFilter
                    ? 'They haven’t posted in this conversation.'
                    : 'Nothing here matches that filter.'}
              </p>
            </div>
          ) : (
            <ul className="py-1">
              {results.map((message) => {
                const row = (
                  <MessageSearchResultRow
                    senderName={message.senderName || message.senderId}
                    content={message.content}
                    createdAt={message.createdAt}
                    terms={terms}
                  />
                );

                return (
                  <li key={message.messageId}>
                    {onSelectResult ? (
                      <DialogClose asChild>
                        <button
                          type="button"
                          onClick={() => onSelectResult(message.messageId)}
                          className="w-full text-left px-4 py-2.5 hover:bg-muted/60 transition-colors"
                        >
                          {row}
                        </button>
                      </DialogClose>
                    ) : (
                      <div className="px-4 py-2.5">{row}</div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {hasNextPage && (
            <div className="p-2 border-t border-border">
              <button
                type="button"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="w-full py-2 text-sm text-primary hover:bg-muted/60 rounded-lg transition-colors disabled:opacity-50"
              >
                {isFetchingNextPage ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
